import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, WifiOff } from 'lucide-react';
import { Header } from './Header';
import { BottomNav } from './BottomNav';
import { AnimatedOutlet } from './AnimatedOutlet';
import { Toast } from '../shared/Toast';
import { OfflineBanner } from '../shared/OfflineBanner';
import { PullToRefresh } from '../shared/PullToRefresh';
import { AchievementCelebration } from '../profile/AchievementCelebration';
import { useApp } from '../../context/AppContext';
import { OnboardingWalkthrough, hasSeenOnboarding } from '../onboarding/OnboardingWalkthrough';

export function AppShell() {
  const { toasts, dismissToast, celebratingAchievement, dismissCelebration, refreshData, loadError, loading, cachedAt } = useApp();
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(() => !hasSeenOnboarding());
  const [offlineToast, setOfflineToast] = useState<string | null>(null);

  // Process pending /add/:handle from pre-auth QR scan or native deep link
  const { sendFriendRequest } = useApp();
  useEffect(() => {
    const processPending = () => {
      const pendingHandle = localStorage.getItem('rostfound_pending_add');
      if (pendingHandle) {
        localStorage.removeItem('rostfound_pending_add');
        sendFriendRequest(pendingHandle).then(() => {
          navigate('/friends', { replace: true });
        });
      }
    };
    processPending();
    window.addEventListener('pending-friend-add', processPending);
    return () => window.removeEventListener('pending-friend-add', processPending);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link from overlap notifications → Calendar (hub)
  useEffect(() => {
    const handler = () => navigate('/');
    window.addEventListener('navigate-to-overlaps', handler);
    return () => window.removeEventListener('navigate-to-overlaps', handler);
  }, [navigate]);

  // Always-mounted listener for shared content from iOS share extension
  // Navigates to Calendar and opens roster import regardless of active tab
  useEffect(() => {
    const handler = () => {
      navigate('/');
      setTimeout(() => window.dispatchEvent(new Event('open-roster-import')), 100);
    };
    window.addEventListener('shared-roster-text-ready', handler);
    window.addEventListener('shared-roster-image-ready', handler);
    return () => {
      window.removeEventListener('shared-roster-text-ready', handler);
      window.removeEventListener('shared-roster-image-ready', handler);
    };
  }, [navigate]);

  // Replay onboarding from Settings — navigate to calendar first
  useEffect(() => {
    const handler = () => {
      navigate('/');
      setShowOnboarding(true);
    };
    window.addEventListener('replay-onboarding', handler);
    return () => window.removeEventListener('replay-onboarding', handler);
  }, [navigate]);

  // Listen for offline mutation attempts
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      const action = detail.action ?? 'do that';
      if (detail.queued) {
        setOfflineToast(`Friend request queued — will send when online`);
      } else {
        setOfflineToast(`Can't ${action} while offline`);
      }
    };
    window.addEventListener('offline-mutation', handler);
    return () => window.removeEventListener('offline-mutation', handler);
  }, []);

  // Auto-dismiss offline toast
  useEffect(() => {
    if (!offlineToast) return;
    const timer = setTimeout(() => setOfflineToast(null), 3000);
    return () => clearTimeout(timer);
  }, [offlineToast]);

  const activeToast = toasts[0];

  return (
    <div id="app-shell" className="w-full mx-auto h-dvh flex flex-col relative overflow-hidden bg-slate-900">
      {showOnboarding && (
        <OnboardingWalkthrough
          onComplete={(action) => {
            setShowOnboarding(false);
            if (action === 'import-roster') {
              window.dispatchEvent(new Event('open-roster-import'));
            }
          }}
        />
      )}

      {/* Toast — max 1 at a time */}
      {activeToast && (
        <div className="absolute left-0 right-0 z-[110]" style={{ top: 'var(--sat, 0px)' }}>
          <Toast
            key={activeToast.id}
            message={activeToast.message}
            subtext={activeToast.subtext}
            icon={<Users size={16} />}
            onDismiss={() => dismissToast(activeToast.id)}
          />
        </div>
      )}

      {/* Achievement celebration overlay — suppress during onboarding */}
      {celebratingAchievement && !showOnboarding && (
        <AchievementCelebration
          achievement={celebratingAchievement}
          onDismiss={dismissCelebration}
        />
      )}

      {/* Offline mutation toast */}
      {offlineToast && (
        <div className="absolute left-0 right-0 z-[110]" style={{ top: 'var(--sat, 0px)' }}>
          <Toast
            message={offlineToast}
            icon={<WifiOff size={16} />}
            variant="error"
            onDismiss={() => setOfflineToast(null)}
          />
        </div>
      )}

      <Header />
      <OfflineBanner cachedAt={cachedAt} />
      {loadError && !loading && (
        <button
          onClick={refreshData}
          className="w-full px-4 py-2 bg-red-500/10 border-b border-red-500/20 text-red-400 text-sm text-center active:bg-red-500/20 transition-colors"
        >
          No internet connection. Tap to retry.
        </button>
      )}
      <PullToRefresh onRefresh={refreshData}>
        <AnimatedOutlet />
      </PullToRefresh>
      <BottomNav />
    </div>
  );
}
